/** @jsx jsx */
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Box, Button, Heading, jsx, Text } from 'theme-ui'
import {
  useAnalytics,
  useTranslate,
} from '@chordcommerce/gatsby-theme-autonomy'
import FormInput from '~/components/Generic/Form/Input'
import FormTextarea from '~/components/Generic/Form/Textarea'
import { useAria } from '~/hooks/components/use-aria'

const SimpleContactForm = ({ name, heading }) => {
  const { trackEmailCaptured } = useAnalytics()
  const { updateAriaLive } = useAria()
  const { register, handleSubmit, errors } = useForm()
  const anchorRef = useRef(null)
  const [submitted, setSubmitted] = useState(false)
  const translate = useTranslate()

  const onSubmit = async (data) => {
    const { email } = data

    // Submit to Netlify
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(data).toString(),
    })
      .then(() => {
        trackEmailCaptured({
          emailCapture: {
            email: email,
            page: 'simple-contact-form',
            component: `${name}-form`,
            website: window.location.hostname,
          },
        })
        setSubmitted(true)
        updateAriaLive('Your submission was successful.')
      })
      .catch((err) => console.error(err, { source: 'SimpleContactForm' }))
  }

  return (
    <Box>
      {heading && (
        <Heading as="h3" variant="title5" mb={[2, 3]}>
          {heading}
        </Heading>
      )}
      {!submitted && (
        <form
          name={name}
          method="POST"
          data-netlify="true"
          netlify-honeypot="nhp"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ position: 'relative', textAlign: 'left' }}
        >
          <Box
            id="schedule"
            ref={anchorRef}
            sx={{ position: 'absolute', top: '-420px' }}
          />
          <input type="hidden" name="nhp" ref={register()} />
          <input type="hidden" name="form-name" value={name} ref={register()} />
          <Box mb={[2, 3]}>
            <FormInput
              name="email"
              title={'Email'}
              label={'Email'}
              aria-label={'Email'}
              required={true}
              type="email"
              error={
                errors?.['email']?.type === 'required' &&
                'This field is required.'
              }
              ref={register({
                required: true,
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: translate('validation.invalid_email'),
                },
              })}
            />
          </Box>
          <Box mb={[2, 3]}>
            <FormTextarea
              name="message"
              title={'Message'}
              label={'Message'}
              aria-label={'Message'}
              required={true}
              error={
                errors?.['message']?.type === 'required' &&
                'This field is required.'
              }
              ref={register({ required: true })}
            />
          </Box>
          <Button sx={{ width: ['100%', 'auto'] }} type="submit">
            {'Send message'}
          </Button>
        </form>
      )}
      {submitted && (
        <Text as="p" variant="body1">
          {'Thanks for reaching out! We will be in touch with you shortly.'}
        </Text>
      )}
    </Box>
  )
}

export default SimpleContactForm
